/**
 * A small pure PNG decoder → RGBA, so pixel-strip data can be read without a
 * canvas (no color management or premultiplied-alpha surprises) and tested in
 * Node. Supports non-interlaced images: 8-bit grayscale / RGB / gray+alpha /
 * RGBA, and palette images at 1, 2, 4 or 8 bits. Anything else throws
 * `UnsupportedPngError`; callers may fall back to the browser's decoder.
 */
import { concatBytes, inflateLimited } from './bytes';
import { ImportError } from './errors';
import type { RgbaImage } from './pixel-codec';
import { readChunks } from './png-chunks';

/** Refuse absurd dimensions before allocating (≈ 4096 × 4096). */
export const MAX_PIXELS = 16_777_216;

export class UnsupportedPngError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'UnsupportedPngError';
  }
}

const CHANNELS: Partial<Record<number, number>> = { 0: 1, 2: 3, 3: 1, 4: 2, 6: 4 };

function paeth(a: number, b: number, c: number): number {
  const p = a + b - c;
  const pa = Math.abs(p - a);
  const pb = Math.abs(p - b);
  const pc = Math.abs(p - c);
  if (pa <= pb && pa <= pc) return a;
  return pb <= pc ? b : c;
}

/** Reverses PNG scanline filters in place; returns the unfiltered rows without filter bytes. */
function unfilter(raw: Uint8Array, height: number, stride: number, bpp: number): Uint8Array {
  const out = new Uint8Array(height * stride);
  for (let y = 0; y < height; y++) {
    const filter = raw[y * (stride + 1)];
    const src = y * (stride + 1) + 1;
    const row = y * stride;
    const prev = row - stride;
    for (let x = 0; x < stride; x++) {
      const value = raw[src + x] ?? 0;
      const a = x >= bpp ? (out[row + x - bpp] ?? 0) : 0;
      const b = y > 0 ? (out[prev + x] ?? 0) : 0;
      const c = x >= bpp && y > 0 ? (out[prev + x - bpp] ?? 0) : 0;
      let predicted: number;
      switch (filter) {
        case 0:
          predicted = 0;
          break;
        case 1:
          predicted = a;
          break;
        case 2:
          predicted = b;
          break;
        case 3:
          predicted = (a + b) >> 1;
          break;
        case 4:
          predicted = paeth(a, b, c);
          break;
        default:
          throw new ImportError('corrupt', `Unknown PNG filter ${String(filter)}`);
      }
      out[row + x] = (value + predicted) & 0xff;
    }
  }
  return out;
}

export function decodePng(bytes: Uint8Array): RgbaImage {
  const chunks = readChunks(bytes);
  const header = chunks[0]?.data ?? new Uint8Array(0);
  if (header.length !== 13) throw new ImportError('corrupt', 'Bad IHDR');
  const view = new DataView(header.buffer, header.byteOffset, header.byteLength);
  const width = view.getUint32(0);
  const height = view.getUint32(4);
  const [bitDepth = 0, colorType = 0, , , interlace = 0] = header.subarray(8);
  const channels = CHANNELS[colorType];

  if (width === 0 || height === 0) throw new ImportError('corrupt', 'Empty PNG');
  if (width * height > MAX_PIXELS) throw new ImportError('too-large', 'PNG dimensions too large');
  if (channels === undefined)
    throw new ImportError('corrupt', `Bad color type ${String(colorType)}`);
  if (interlace !== 0) throw new UnsupportedPngError('Interlaced PNGs are not supported');
  const paletted = colorType === 3;
  if (paletted ? ![1, 2, 4, 8].includes(bitDepth) : bitDepth !== 8) {
    throw new UnsupportedPngError(`Unsupported bit depth ${String(bitDepth)}`);
  }

  const palette = chunks.find((chunk) => chunk.type === 'PLTE')?.data;
  const transparency = chunks.find((chunk) => chunk.type === 'tRNS')?.data;
  if (paletted && !palette) throw new ImportError('corrupt', 'Palette PNG without PLTE');

  const stride = Math.ceil((width * channels * bitDepth) / 8);
  const bpp = Math.max(1, (channels * bitDepth) >> 3);
  const compressed = concatBytes(
    chunks.filter((chunk) => chunk.type === 'IDAT').map((chunk) => chunk.data),
  );
  const raw = inflateLimited(compressed, 'zlib', (stride + 1) * height);
  if (raw.length !== (stride + 1) * height) throw new ImportError('corrupt', 'Truncated PNG data');
  const pixels = unfilter(raw, height, stride, bpp);

  const data = new Uint8ClampedArray(width * height * 4);
  for (let y = 0; y < height; y++) {
    const row = pixels.subarray(y * stride, (y + 1) * stride);
    for (let x = 0; x < width; x++) {
      const o = (y * width + x) * 4;
      if (paletted) {
        const perByte = 8 / bitDepth;
        const shift = 8 - bitDepth * ((x % perByte) + 1);
        const index = ((row[Math.floor(x / perByte)] ?? 0) >> shift) & ((1 << bitDepth) - 1);
        data[o] = palette?.[index * 3] ?? 0;
        data[o + 1] = palette?.[index * 3 + 1] ?? 0;
        data[o + 2] = palette?.[index * 3 + 2] ?? 0;
        data[o + 3] = transparency?.[index] ?? 255;
        continue;
      }
      const p = x * channels;
      const gray = colorType === 0 || colorType === 4;
      data[o] = row[p] ?? 0;
      data[o + 1] = row[gray ? p : p + 1] ?? 0;
      data[o + 2] = row[gray ? p : p + 2] ?? 0;
      data[o + 3] = channels === 2 || channels === 4 ? (row[p + channels - 1] ?? 0) : 255;
    }
  }
  return { width, height, data };
}
